/**
 * Pure helpers for the survey voice-capture path. Kept side-effect-free so they
 * can be unit-tested without mocking the MediaRecorder/getUserMedia stack (the
 * repo verifies the media wiring itself via e2e, not jsdom mocks).
 */

const DEFAULT_BAR_COUNT = 7;
const LEVEL_FLOOR = 0.5;

/**
 * Map analyser frequency data to a set of visualizer bar heights in [0.5, 1].
 * Deterministic: no Math.random(), so the visualizer no longer churns per frame.
 */
export function computeAudioLevels(
  dataArray: Uint8Array,
  barCount: number = DEFAULT_BAR_COUNT,
): number[] {
  const bufferLength = dataArray.length || 1;
  let sum = 0;
  for (let i = 0; i < dataArray.length; i++) sum += dataArray[i];
  const average = sum / bufferLength;
  const normalizedLevel = Math.min(1, average / 50); // conservative threshold (was inline)

  const centerIndex = Math.floor(barCount / 2);
  return Array.from({ length: barCount }, (_, i) => {
    const distanceFromCenter = Math.abs(i - centerIndex);
    const baseMultiplier = 1 - distanceFromCenter * 0.15;
    const scaledLevel = normalizedLevel * 1.5 + LEVEL_FLOOR;
    return Math.max(LEVEL_FLOOR, Math.min(1, scaledLevel * baseMultiplier));
  });
}

/** True when a MediaRecorder data chunk proves capture is actually live. */
export function isReadyChunk(size: number, minBytes = 0): boolean {
  return size > minBytes;
}

/** True when a finished recording is large enough to be worth transcribing. */
export function isBlobLongEnough(size: number, minBytes: number): boolean {
  return size >= minBytes;
}
